import { useEffect, useRef } from 'react';

// Which hook instance last wrote each custom property. An unmounting instance clears only a
// value it wrote, so the next page starts from the stylesheet fallback instead of inheriting
// this page's height, and instances on different pages that share a name cannot wipe each
// other's value.
const varOwners = new Map();

const useElementHeightCssVar = (selector, cssVarName, stopObserving = false) => {
  const resizeObserverRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const currentElementRef = useRef(null);
  const ownerRef = useRef({});

  useEffect(() => {
    const updateCssVar = (el) => {
      const height = el?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(cssVarName, `${height}px`);
      varOwners.set(cssVarName, ownerRef.current);
    };

    const observeElement = (el) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      if (el && !stopObserving) {
        currentElementRef.current = el;
        resizeObserverRef.current = new ResizeObserver(() => {
          updateCssVar(el);
        });
        resizeObserverRef.current.observe(el);
        updateCssVar(el);
      } else {
        currentElementRef.current = el;
        if (stopObserving) {
          return;
        }
        updateCssVar(null);
      }
    };

    const findAndObserve = () => {
      try {
        const el = document.querySelector(selector);
        // Check if it's actually a different DOM element, not just reference equality
        if (el !== currentElementRef.current) {
          observeElement(el);
        }
      } catch {
        console.error(`Invalid selector "${selector}" passed to useElementHeightCssVar`);
      }
    };

    if (stopObserving) {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      return;
    }

    findAndObserve();

    mutationObserverRef.current = new MutationObserver(findAndObserve);
    mutationObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true, // Add this to catch attribute changes
      attributeFilter: ['style', 'class'] // Watch for style/class changes that might show/hide elements
    });

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      // A re-run must observe again from scratch; with the old element still recorded it
      // would be skipped as unchanged and never get a new ResizeObserver.
      currentElementRef.current = null;
    };
  }, [selector, cssVarName, stopObserving]);

  useEffect(() => {
    const owner = ownerRef.current;
    return () => {
      if (varOwners.get(cssVarName) === owner) {
        varOwners.delete(cssVarName);
        document.documentElement.style.removeProperty(cssVarName);
      }
    };
  }, [cssVarName]);
};

export default useElementHeightCssVar;
